import { RouteIds } from '../types';
import { Observable } from 'rxjs';
import { Flow } from '@lx/quote-tool/lite/shared/types';
import { GoogleAnalyticsParametersType, GoogleAnalyticsQuoteToolLiteQuote } from './google-analytics-parameters.type';

/**
 * A type that represents a partial record mapping RouteIds to functions that return observables of partial GoogleAnalyticsQuote objects.
 * Each function is used to build the quote data for each step in the quote process.
 * This allows for dynamic construction of the data layer based on the current step in the quote process.
 */
export type QuoteFieldsBuilders = Partial<Record<RouteIds, () => Observable<Partial<GoogleAnalyticsQuoteToolLiteQuote>>>>;

/**
 * Interface that defines the strategy for building the quote information on data layer object
 * depending on the flow (e.g., Adult, Junior, Online, AYA).
 */
export interface QuoteDataLayerStrategy {
  readonly flow: Flow;

  /**
   * A map of functions that build the quote fields for each step in the quote process.
   * Each function returns an observable that emits a partial GoogleAnalyticsQuote object.
   * The keys are the route IDs corresponding to each step.
   * This allows for dynamic construction of the data layer based on the current step in the quote process.
   */
  quoteFieldsBuilders: QuoteFieldsBuilders | undefined;

  /**
   * Initializes the map of functions that build the quote fields for each step in the quote process.
   * Each function returns an observable that emits a partial GoogleAnalyticsQuote object.
   * The keys are the route IDs corresponding to each step.
   * This allows for dynamic construction of the data layer based on the current step in the quote process.
   */
  initializeDataLayerStepsBuilders(): void;

  /**
   * Builds the data layer object with quote information based on the current flow and step in the quote process.
   * Returns an observable that emits a partial GoogleAnalyticsParametersType object to be pushed to the data layer.
   */
  buildDataLayer(): Observable<Partial<GoogleAnalyticsParametersType>>;
}
